/**
 * Requestors — TanStack Query hooks (OPEN-053, ClickUp 86bc5hjn1).
 *
 * The people who ask for loads, and their two assignment slots per site/well.
 *
 * Reads gate on VIEW_HAULER_CATALOG (a plain dispatcher must see both requestors at ticket
 * entry); writes on MANAGE_HAULER_CATALOG (Dispatch Manager and up). The server is the
 * authoritative gate — `canManage` in the UI only hides affordances.
 *
 * Mirrors `features/sites-operators/hooks.ts`, which is the house idiom for a tenant catalog.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Requestor {
  readonly id: string;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
  readonly company: string | null;
  readonly isActive: boolean;
  /** How many sites/wells this person is on — shown so a deactivate decision is informed. */
  readonly assignmentCount: number;
  /** Phase 17.8 (D-13) — "send load emails" checkbox; absent on older payloads = on. */
  readonly loadEmailsEnabled?: boolean;
  /** Set only by the requestor's own unsubscribe link (D-09). */
  readonly unsubscribed?: boolean;
  /** 17.6 — 'OK' | 'BOUNCING' | 'COMPLAINED'. */
  readonly emailStatus?: string;
}

/** What a site/well assignment target looks like on the wire. */
export type AssignmentTargetType = 'TANK_BATTERY' | 'WELL';

export interface AssignedRequestor {
  readonly id: string;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
  readonly company: string | null;
  readonly isActive: boolean;
}

export interface AssignmentSlots {
  readonly targetType: AssignmentTargetType;
  readonly targetId: string;
  readonly slot1: AssignedRequestor | null;
  readonly slot2: AssignedRequestor | null;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const REQUESTORS_KEY = ['requestors'] as const;
export const ASSIGNMENTS_KEY = (
  targetType: AssignmentTargetType,
  targetId: string,
): readonly string[] => ['requestor-assignments', targetType, targetId];

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useRequestors(
  activeOnly = false,
): ReturnType<typeof useApiQuery<{ requestors: readonly Requestor[] }>> {
  const opts = useApiOpts();
  const path = activeOnly ? '/v1/requestors?activeOnly=true' : '/v1/requestors';
  return useApiQuery<{ requestors: readonly Requestor[] }>(opts, path, {
    queryKey: [...REQUESTORS_KEY, activeOnly ? 'active' : 'all'],
    staleTime: 30_000,
  });
}

/**
 * The two slots on one site/well. Used by the detail panes AND by ticket entry, where it
 * fires on site selection — small and cacheable, rather than inflating the pickup-options
 * payload the New Job modal already loads.
 *
 * `targetId === null` disables the query (nothing selected yet).
 */
export function useAssignments(
  targetType: AssignmentTargetType,
  targetId: string | null,
): ReturnType<typeof useApiQuery<AssignmentSlots>> {
  const opts = useApiOpts();
  return useApiQuery<AssignmentSlots>(
    opts,
    `/v1/requestor-assignments?targetType=${targetType}&targetId=${targetId ?? ''}`,
    {
      queryKey: ASSIGNMENTS_KEY(targetType, targetId ?? '__none__'),
      enabled: targetId !== null,
      staleTime: 15_000,
    },
  );
}

/**
 * One row of the tenant-wide assignment read — thin by design.
 *
 * The person is referenced by id and carried once in `AllAssignments.requestors`, not nested
 * here: a tenant with 500 sites pointing at the same two people would otherwise repeat those
 * two people 1,000 times.
 */
export interface AssignmentRow {
  readonly targetType: AssignmentTargetType;
  readonly targetId: string;
  readonly slot: 1 | 2;
  readonly requestorId: string;
}

export interface AllAssignments {
  /** Only the people who actually hold a slot — NOT a substitute for `useRequestors`. */
  readonly requestors: readonly AssignedRequestor[];
  readonly assignments: readonly AssignmentRow[];
}

/**
 * Every assignment in the tenant, in one request — the read the bulk editor runs on.
 *
 * The per-target `useAssignments` cannot serve that screen: firing it once per site is slow,
 * and the confirm-before-clearing count would then be computed from whatever had loaded so
 * far, which is exactly the number the dispatcher is being asked to trust.
 */
export function useAllAssignments(): ReturnType<typeof useApiQuery<AllAssignments>> {
  const opts = useApiOpts();
  return useApiQuery<AllAssignments>(opts, '/v1/requestor-assignments/all', {
    queryKey: ['requestor-assignments', 'all'],
    staleTime: 15_000,
  });
}

/** One site or well a given requestor holds a slot on, with its display name resolved. */
export interface RequestorAssignment {
  readonly targetType: AssignmentTargetType;
  readonly targetId: string;
  readonly slot: 1 | 2;
  readonly name: string | null;
  readonly customer: string | null;
  /** API number for a well, customer abbreviation for a site. */
  readonly sub: string | null;
}

export interface RequestorAssignments {
  readonly requestor: { readonly id: string; readonly name: string };
  readonly assignments: readonly RequestorAssignment[];
}

/**
 * Everywhere ONE requestor is assigned — the reverse of `useAssignments`.
 *
 * Server-joined names, deliberately. Deriving this from `useAllAssignments` would need the
 * whole site+well catalog in the browser to resolve ids to labels, and the wells half of that
 * load truncates at 500 without saying so.
 */
export function useRequestorAssignments(
  id: string | null,
): ReturnType<typeof useApiQuery<RequestorAssignments>> {
  const opts = useApiOpts();
  return useApiQuery<RequestorAssignments>(opts, `/v1/requestors/${id ?? ''}/assignments`, {
    queryKey: ['requestors', id ?? '__none__', 'assignments'],
    enabled: id !== null,
    staleTime: 15_000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export interface RequestorBody {
  name?: string;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
  isActive?: boolean;
  loadEmailsEnabled?: boolean;
}

export function useCreateRequestor(
  onDone: (id: string) => void,
): ReturnType<typeof useApiMutation<RequestorBody, { requestor: Requestor }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<RequestorBody, { requestor: Requestor }>(opts, '/v1/requestors', 'POST', {
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: REQUESTORS_KEY });
      onDone(res.requestor.id);
    },
  });
}

export function useUpdateRequestor(
  id: string,
): ReturnType<typeof useApiMutation<RequestorBody, { requestor: Requestor }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<RequestorBody, { requestor: Requestor }>(
    opts,
    `/v1/requestors/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: REQUESTORS_KEY });
        // An edit can change a name shown inside an assignment pane.
        void qc.invalidateQueries({ queryKey: ['requestor-assignments'] });
      },
    },
  );
}

/**
 * Set/clear the slots on ONE target.
 *
 * The tri-state is the contract and is preserved exactly: omit a slot to leave it alone,
 * pass an id to set it, pass `null` to CLEAR it — and `null` requires `confirmClear: true`.
 * Never send `null` to mean "unchanged".
 */
export interface SetAssignmentsBody {
  targetType: AssignmentTargetType;
  targetId: string;
  slot1?: string | null;
  slot2?: string | null;
  confirmClear?: boolean;
}

export function useSetAssignments(): ReturnType<
  typeof useApiMutation<SetAssignmentsBody, { ok: boolean }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<SetAssignmentsBody, { ok: boolean }>(
    opts,
    '/v1/requestor-assignments',
    'PUT',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['requestor-assignments'] });
        void qc.invalidateQueries({ queryKey: REQUESTORS_KEY });
      },
    },
  );
}

export interface BulkAssignBody {
  targets: { targetType: AssignmentTargetType; targetId: string }[];
  slot1?: string | null;
  slot2?: string | null;
  confirmClear?: boolean;
}

export interface BulkAssignResult {
  readonly updated: number;
  readonly failed: readonly { targetId: string; reason: string }[];
}

/**
 * Bulk set/clear across many targets. Returns 200 even when some targets fail — per-target
 * reasons ride the success payload, because the shared fetch wrapper cannot surface an error
 * body. Callers MUST render `failed`; treating a 200 as "all good" is the bug this shape
 * exists to prevent.
 */
export function useBulkAssign(): ReturnType<
  typeof useApiMutation<BulkAssignBody, BulkAssignResult>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<BulkAssignBody, BulkAssignResult>(
    opts,
    '/v1/requestor-assignments/bulk',
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['requestor-assignments'] });
        void qc.invalidateQueries({ queryKey: REQUESTORS_KEY });
      },
    },
  );
}
